import { API_BASE_URL } from 'src/globals';
import { APIResponseType } from 'src/Library/Types';
import { GET, POST } from './apiHandler';
import { DOCUMENT_PARSE_STATUS, DOCUMENT_PARSE_TRIGGER } from './routes';

/**
 * Document parse & field prefill (ISS-292181).
 *
 * The backend never writes anything task-visible: it returns extracted values as SUGGESTIONS on the
 * parse job, and the FE fills on-screen inputs that are still empty. Values only reach the database
 * when the user clicks Submit on the task, through the existing task-save path.
 */
export enum DocumentParseStatus {
  QUEUED = 'QUEUED',
  SUBMITTED = 'SUBMITTED',
  PARSED = 'PARSED',
  PARSE_FAILED = 'PARSE_FAILED',
  SUBMIT_FAILED = 'SUBMIT_FAILED',
}

export enum DocumentParseErrorCode {
  /**
   * Parsing is not configured for this task or document type. The trigger fires on EVERY uploaded
   * document, so this is the normal answer for most of them — it means "not applicable here", not
   * "something went wrong", and is the one code the UI stays quiet about.
   */
  NOT_AVAILABLE = 'EDOCPARSE001',
}

export const TERMINAL_DOCUMENT_PARSE_STATUSES: DocumentParseStatus[] = [
  DocumentParseStatus.PARSED,
  DocumentParseStatus.PARSE_FAILED,
  DocumentParseStatus.SUBMIT_FAILED,
];

/**
 * Values arrive already in the shape the matching input expects — DATE as epoch ms, CHECKBOX as the
 * string 'true'/'false', DROPDOWN as the option's `value`, WEIGHT_UNIT as one of
 * `masterData.execution.validWeightUnits`. No client-side coercion is needed.
 */
export interface DocumentParseExtractedField {
  fieldTemplateId: string;
  /** The backend's mapping key — `field_template.external_code`. Informational here. */
  fieldExternalCode: string;
  fieldName: string;
  type: string;
  /**
   * A string array exactly for the list types (`CONTAINER_NUMBER_LIST`, `BL_NUMBER_LIST`), which
   * render through `useSEGenericMultiSelect` and so hold an array. The backend splits its agent's
   * comma-separated string, and always sends an array for those types even when it holds one entry,
   * so nothing here has to branch on arity. Scalar for every other type.
   */
  value: string | number | string[];
  rawValue: string;
}

export interface DocumentParseRejectedValue {
  responseKey: string;
  fieldExternalCode: string;
  fieldName?: string;
  rawValue: string;
  reason: string;
}

/**
 * Running out of attempts is a normal outcome, not an error: the backend answers 200 with
 * `parseJobId: null` and `isAttemptLimitReached`, because the attempt window frees a slot on its own.
 * An error response could not have carried `attemptsResetAt` at all — the API only surfaces a
 * message and an error code from a failure.
 */
export interface TriggerDocumentParseResponse {
  /** Null exactly when `isAttemptLimitReached` — there is no job to poll. */
  parseJobId: string | null;
  attemptsUsed: number;
  attemptsAllowed: number;
  isAttemptLimitReached?: boolean;
  /** Epoch ms at which the next attempt unlocks. Present only while attempts are exhausted. */
  attemptsResetAt?: number;
  message?: string;
}

export interface DocumentParseStatusResponse {
  parseStatus: DocumentParseStatus;
  docType: string;
  extractedFields?: DocumentParseExtractedField[];
  rejectedValues?: DocumentParseRejectedValue[];
  error?: string;
  attemptsUsed: number;
  attemptsAllowed: number;
  /** Epoch ms at which the next attempt unlocks. Present only while attempts are exhausted. */
  attemptsResetAt?: number;
}

/**
 * Called right after the blob upload completes, once per uploaded document. `docType` is the upload
 * slot's document template name and `documentTemplateId` its id — the backend needs both because no
 * `documents` row exists before the task is submitted, so it can infer neither.
 *
 * `docMimeType` is the browser's own `File.type`, passed straight through to the parsing agent as
 * `doc_mime_type` — the backend does not second-guess it. Optional: a browser that cannot identify
 * the file reports an empty string, and the agent then falls back to sniffing the document itself.
 */
export const triggerDocumentParse = (body: {
  shipmentId: string;
  taskId: string;
  docUrl: string;
  docType: string;
  documentTemplateId: string;
  docMimeType?: string;
}): Promise<APIResponseType<TriggerDocumentParseResponse>> => {
  return POST(`${API_BASE_URL}${DOCUMENT_PARSE_TRIGGER}`, body);
};

export const getDocumentParseStatus = (
  parseJobId: string
): Promise<APIResponseType<DocumentParseStatusResponse>> => {
  return GET(`${API_BASE_URL}${DOCUMENT_PARSE_STATUS(parseJobId)}`);
};
